<template>
  <div class="newspaper-wrapper" :class="{'before-mounted': !mounted}">
    <div class="paper-edge top"></div>
    <div class="newspaper-content">
      <div class="pagination-wrap">
        <div class="item" v-for="(n, k) in filtedTotalSlide" :class="{active: k === (index % numPerSlideScope)}" :key="k"></div>
      </div>
      <div class="news-bites">NEWS BITES</div>
      <server-side-only-link
        to="/china-tech-city/news" class="landing-page-button"><icon-arrow-left class="landing-right-arrow"></icon-arrow-left><icon-news-view-landing-page class="landing-page-icon"></icon-news-view-landing-page><span>Back</span>
      </server-side-only-link>
      <div
        v-if="totalSlide > 1"
        class="impression-tracker"
        v-gtm-tracker="arrowImpressionTracker"
        ></div>
      <div class="newspaper-body">
        <h1 v-if="isActive" class="title" :class="{collapsed: titleCollapsed}">{{ node.title }}</h1>
        <div v-else class="title" :class="{collapsed: titleCollapsed}">{{ node.title }}</div>
        <div class="block" v-show="titleCollapsed"></div>
        <div class="scrollable-wrapper"
          ref="scrollableWrapper"
          :style="scrollableWrapperH"
          @scroll.passive="scrollHandler"
          >
          <div class="scrollable-content">
            <div class="info-bar">
              <author-pane :nid="nid" :formatDateHTML="true"></author-pane>
              <share-count
                class="share-count"
                ref="shareCount"
                v-if="mounted && shareUrl && index <= activeIndex"
                :url="shareUrl"
              ></share-count>
            </div>
            <newspaper-body :nid="nid"></newspaper-body>
            <quick-stat :statsByCompany="statsByCompany"></quick-stat>

            <tech-city-carousel
              class="newspaper-carousel"
              v-if="!isEmpty(relatedNodes) && carouselActive"
              :nids="relatedNodes"
              :title="'Articles'"
              :data_key="'news'"
            ></tech-city-carousel>
            <lazy-component v-else>
              <tech-city-carousel
                class="newspaper-carousel"
                v-if="!isEmpty(relatedNodes)"
                :nids="relatedNodes"
                :title="'Articles'"
                :data_key="'news'"
              ></tech-city-carousel>
            </lazy-component>

          </div>
        </div>
        <div class="bottom-buffer"></div>
      </div>
    </div>
    <div class="mask" v-if="!isActive"></div>
  </div>
</template>

<script>
  import isEmpty from 'lodash/isEmpty'
  import map from 'lodash/map'
  import get from 'lodash/get'
  import keys from 'lodash/keys'
  import fill from 'lodash/fill'
  import uniq from 'lodash/uniq'
  import filter from 'lodash/filter'
  import includes from 'lodash/includes'

  import mounted from 'components/mixins/mounted'
  import { mapState, mapGetters } from 'vuex'

  import AuthorPane from 'components/node/AuthorPane.vue'
  import iconNewsViewLandingPage from 'static/svg/tc_news-landing.svg'
  import iconArrowLeft from 'static/svg/icon_arrow_left1.svg'

  export default {
    name: 'newspaper',
    props: {
      index: {
        type: Number,
        required: false,
        default: 0,
      },
      totalSlide: {
        type: Number,
        required: false,
        default: 0,
      },
      isActive: {
        type: Boolean,
        required: false,
        default: true,
      },
      activeIndex: {
        type: Number,
        default: 0,
      },
      activeIndex: {
        type: Number,
        default: 0,
      },
    },
    mixins: [mounted],
    components: {
      AuthorPane,
      iconNewsViewLandingPage,
      iconArrowLeft,
      NewspaperBody: () => import('components/tech-city/news-view/NewspaperBody.vue'),
      QuickStat: () => import('components/tech-city/news-view/QuickStat.vue'),
      TechCityCarousel: () => import('components/tech-city/widgets/TechCityCarousel.vue'),
    },
    data() {
      return {
        titleCollapsed: false,
        scrollableWrapperH: {},
        scrollStop: null,
        numPerSlideScope: 10,
      }
    },
    created() {
      this.$store.dispatch('fetchNode', { nid: this.nid })
    },
    computed: {
      ...mapState(['settings']),
      arrowImpressionTracker() {
        return {
          trackEV: true,
          ec: 'Article News/Arrow',
          ea: 'China Tech City News View/Arrow/Impression',
          el: 'Previous Next',
        }
      },
      relatedArticles() {
        const tid = get(this, 'node.topics[0]')
        const relatedPersonQueue = this.$store.getters.nodeRelatedNodesQueue(`node-related-nodes-${this.nid}`)
        let queue = isEmpty(relatedPersonQueue) ? [] : map(keys(relatedPersonQueue), o => relatedPersonQueue[o])
        if (queue.length < 5) {
          const topicQueue = this.$store.getters.termContentQueue(`taxonomy-term-content-${tid}`)
          queue = [...queue, ...map(keys(topicQueue), o => topicQueue[o])]
        }
        return queue
      },
      nodeRelatedArticles() {
        return get(this, 'node.related_articles', [])
      },
      relatedExplainers() {
        if (!isEmpty(this.node)) {
          return get(this, 'node.related_people', [])
        }
        return []
      },
      relatedNodes() {
        if (!isEmpty(this.relatedExplainers) || !isEmpty(this.relatedArticles) || !isEmpty(this.nodeRelatedArticles)) {
          return uniq([...this.relatedExplainers, ...this.relatedArticles, ...this.nodeRelatedArticles])
        }
        // Add dummy related node to show placeholder
        return fill(Array(5), 'loading')
      },
      statsByCompany() {
        return filter(get(this, 'settings.CHINA_TECH_CITY_NEWS.QUICK_STATS', []), company => includes(get(company, 'nids', []), this.nid))
      },
      carouselActive() {
        return this.index >= this.activeIndex - 1 && this.index <= this.activeIndex + 1
      },
      filtedTotalSlide() {
        const scopeTotal = this.totalSlide % this.numPerSlideScope
        if (this.totalSlide < this.numPerSlideScope) {
          return scopeTotal
        } else {
          if (this.activeSlide + 1 > Math.floor(this.totalSlide / this.numPerSlideScope) * this.numPerSlideScope) {
            return scopeTotal
          } else {
            return this.numPerSlideScope
          }
        }
      },
      shareUrl() {
        return `${this.requestDomain()}${get(this, 'node.relative_url')}`
      },
    },
    methods: {
      isEmpty,
      ...mapGetters(['requestDomain']),
      scrollHandler(e) {
        clearTimeout(this.scrollStop)
        this.scrollStop = setTimeout(() => {
          this.$emit('newspaperOnScrollStop')
        }, 300)
        const wrapper = get(e, 'target')
        this.$emit('newspaperOnScroll')
        this.titleCollapsed = get(wrapper, 'scrollTop') > 0
      },
    },
  }
</script>


<style lang="scss" scoped>
  $newspaperHorizontalPaddingDesktop: rem-calc(30);
  $newspaperHorizontalPaddingDesktopNeg: -#{$newspaperHorizontalPaddingDesktop};
  $newspaperHorizontalPaddingMobile: rem-calc(20);
  $newspaperHorizontalPaddingMobileNeg: -#{$newspaperHorizontalPaddingMobile};

  .newspaper-wrapper {
    color: $greyish-brown;
    width: 80vw;
    height: 100%;
    position: relative;
    overflow: hidden;
    will-change: transform, opacity;
    &.before-mounted{
      margin: auto;
    }
    .mask {
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      z-index: 1000;
    }
    &.swiper-slide-active {
      opacity: 1;
    }
    &.swiper-slide-next, &.swiper-slide-prev {
      opacity: 0.8;

      .newspaper-content .newspaper-body .scrollable-wrapper {
        height: auto;
      }
    }
    @include respond-to('mobile') {
      width: 100vw;
    }

    @include respond-to("hd") {
      width: rem-calc(1250px);
    }

    @include respond-to("largedesktop") {
      width: rem-calc(1200px);
    }

    .newspaper-content {
      padding: rem-calc(0 0 50 0);
      background: $off-white;
      overflow-y: auto;
      .pagination-wrap {
        display: flex;
        justify-content: center;
        .item {
          background-color: $black;
          opacity: .6;
          width: rem-calc(8);
          height: rem-calc(4);
          margin: rem-calc(12 2);
          border-radius: rem-calc(20);
          &.active {
            width: rem-calc(20);
            opacity: 1;
          }
        }
      }

      .news-bites {
        font-size: rem-calc(20);
        text-align: center;
        padding: rem-calc(0 0 5);
        margin: rem-calc(0 $newspaperHorizontalPaddingDesktop);
        border-bottom: rem-calc(1) solid $greyish-brown;
        font-weight: bold;
        @include respond-to('mobile') {
          padding-bottom: 0;
          margin: rem-calc(0 10);
        }
      }

      .newspaper-body {
        margin: rem-calc(0 $newspaperHorizontalPaddingDesktop);

        @include respond-to("mobile") {
          margin: rem-calc(0 $newspaperHorizontalPaddingMobile);
        }

        .title {
          font-family: $barlow;
          font-size: rem-calc(42);
          font-weight: bold;
          font-style: normal;
          font-stretch: normal;
          line-height: normal;
          letter-spacing: normal;
          padding: rem-calc(10);
          margin-bottom: rem-calc(10);
          color: $greyish-brown;
          @include respond-to('mobile') {
            font-size: rem-calc(30);
            line-height: rem-calc(36);
            margin-bottom: rem-calc(15);
            padding: rem-calc(10 0 0);
          }
          &.collapsed {
            font-size: rem-calc(22);
            overflow: hidden;
            text-overflow: ellipsis;
            display: -webkit-box;
            -webkit-line-clamp: 2;
            -webkit-box-orient: vertical;
            padding: rem-calc(10 10 0);
            @include respond-to('mobile') {
              font-size: rem-calc(18);
              line-height: rem-calc(22);
              margin-bottom: rem-calc(10);
            }
          }
        }

        .block {
          width: 100%;
          height: rem-calc(5);
          background-color: $pale;

          @include respond-to("mobile") {
            padding-right: rem-calc(20);
            margin-left: rem-calc(-10);
          }
        }

        .scrollable-wrapper {
          height: calc(100vh - 222px);
          margin: 0 $newspaperHorizontalPaddingDesktopNeg;
          padding: rem-calc(0 $newspaperHorizontalPaddingDesktop);
          overflow-x: hidden;

          @include respond-to("mobile") {
            margin: 0 $newspaperHorizontalPaddingMobileNeg;
            padding: rem-calc(0 $newspaperHorizontalPaddingMobile);
            height: calc(100vh - 128px);
          }
        }

        /deep/ {
          .info-bar {
            display: flex;
            justify-content: space-between;
            align-items: flex-end;
            background-color: $pale;
            padding: rem-calc(5 10);
            @include respond-to('mobile') {
              margin-left: rem-calc(-10);
              margin-right: rem-calc(-10);
            }
            .author-pane {
              @include respond-to('mobile') {
                margin-top: 0;
                width: 70%;
              }
              .author-info {
                &.china-tech-city-news, &.default{
                  margin-top: 0;
                  text-align: left;
                }
              }

              .author-image {
                display: none;
              }

              .author-list {
                font-size: rem-calc(14);
                font-weight: bold;
                margin-bottom: 0;
                .authors:before {
                  font-weight: normal;
                }
              }
              .published-date {
                color: $greyish-brown;
                text-align: left;
                @include respond-to('mobile') {
                  .date-wrap {
                    .timezone {
                      display: block;
                    }
                  }
                }
              }
            }

            .share-count {
              text-align: center;
              line-height: initial;
              @include respond-to('desktop') {
                line-height: rem-calc(18);
              }
              .share-number {
                font-size: rem-calc(22);
                font-weight: 700;
                padding-right: rem-calc(5);
              }
              .share-text {
                font-size: rem-calc(12);
              }
            }
          }
        }

        .bottom-buffer {
          height: 50vh;
          background-color: $off-white;
        }
      }
    }

    .paper-edge {
      background-image: url('/static/svg/newspaper-edge-top.svg');
      height: rem-calc(8);
      background-position: 0 0;
      background-repeat: repeat;
    }
  }
  .newspaper-carousel {
    margin-top: rem-calc(30);
    padding: rem-calc(0 10);

    @include respond-to("mobile") {
      padding: rem-calc(0 0 130);
    }
  }

  .impression-tracker {
    width: rem-calc(1);
    height: rem-calc(1);
  }

  .landing-page-button {
    position: absolute;
    color: $darker-gray-2;
    font-family: $barlow;
    font-size: rem-calc(12);
    font-weight: 500;
    background: $dirty-yellow;
    border-radius: rem-calc(14);
    padding: rem-calc(5 14 5 9);
    left: rem-calc(40);
    top: rem-calc(25);
    svg {
      display: inline-block;
      fill: $darker-gray-2;
      vertical-align: middle;
    }
    .landing-right-arrow {
       width: rem-calc(6);
       height: rem-calc(10);
       margin-right: rem-calc(7);
    }
    .landing-page-icon {
      width: rem-calc(22);
      height: rem-calc(18);
      margin-right: rem-calc(5);
      margin-top: rem-calc(-1);
    }
    @include respond-to("mobile") {
      top: rem-calc(25);
      left: rem-calc(13);
      span {
        display: none;
      }
      .landing-page-icon {
        margin-right: 0;
      }
    }
  }
</style>



// WEBPACK FOOTER //
// components/tech-city/news-view/Newspaper.vue